import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bug, Copy, Loader2, Plus, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ColumnSelectorButton } from "@/components/ui/ColumnSelectorButton";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { AppLayout } from "@/components/layout/AppLayout";
import { formatDateTime } from "@/lib/utils";
import {
  useColumnVisibility,
  type ColumnDef,
} from "@/hooks/useColumnVisibility";
import { useUserOptions } from "@/modules/users/hooks/useUsers";
import { useCategoryItems } from "@/modules/dropdowns/hooks/useDropdowns";
import {
  useIssueList,
  useIssue,
  useIssueProducts,
  useProductModules,
  useDeleteIssue,
} from "../hooks/useIssues";
import { NewIssueDrawer } from "../components/NewIssueDrawer";
import { IssueStatusBadge, PriorityBadge } from "../components/badges";
import type { IssueListItem } from "../types";

const ISSUE_COLUMNS: ColumnDef[] = [
  { key: "code", label: "#" },
  { key: "title", label: "Title" },
  { key: "productName", label: "Product" },
  { key: "moduleName", label: "Module" },
  { key: "category", label: "Category", defaultVisible: false },
  { key: "priority", label: "Priority" },
  { key: "status", label: "Status" },
  { key: "assigneeName", label: "Assignee" },
  { key: "reportedByName", label: "Reported By" },
  { key: "createdAt", label: "Created" },
];

function humanize(v: string | null): string {
  if (!v) return "—";
  return v.replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function IssueRow({
  issue,
  onDelete,
  onDuplicate,
  onEdit,
  isVisible,
}: {
  issue: IssueListItem;
  onDelete: (id: string, name: string) => void;
  onDuplicate: (i: IssueListItem) => void;
  onEdit: (i: IssueListItem) => void;
  isVisible: (key: string) => boolean;
}) {
  const navigate = useNavigate();
  return (
    <tr
      className="cursor-pointer transition-colors hover:bg-[hsl(210,50%,98%)]"
      onClick={() => navigate(`/qa/issues/${issue.id}`)}
    >
      {isVisible("code") && (
        <td className="px-6 py-4">
          <span className="font-mono text-xs text-muted-foreground">{issue.code}</span>
        </td>
      )}
      {isVisible("title") && (
        <td className="px-6 py-4">
          <div className="font-medium text-foreground">{issue.title}</div>
        </td>
      )}
      {isVisible("productName") && (
        <td className="px-6 py-4 text-sm text-muted-foreground">
          {issue.productName ?? "—"}
        </td>
      )}
      {isVisible("moduleName") && (
        <td className="px-6 py-4 text-sm text-muted-foreground">
          {issue.moduleName ?? "—"}
          {issue.submoduleName ? (
            <span className="text-muted-foreground/50"> / {issue.submoduleName}</span>
          ) : null}
        </td>
      )}
      {isVisible("category") && (
        <td className="px-6 py-4 text-sm text-muted-foreground">
          {humanize(issue.category)}
        </td>
      )}
      {isVisible("priority") && (
        <td className="px-6 py-4">
          <PriorityBadge priority={issue.priority} />
        </td>
      )}
      {isVisible("status") && (
        <td className="px-6 py-4">
          <IssueStatusBadge status={issue.status} />
        </td>
      )}
      {isVisible("assigneeName") && (
        <td className="px-6 py-4 text-sm text-muted-foreground">
          {issue.assigneeName ?? "—"}
        </td>
      )}
      {isVisible("reportedByName") && (
        <td className="px-6 py-4 text-sm text-muted-foreground">
          {issue.reportedByName ?? "—"}
        </td>
      )}
      {isVisible("createdAt") && (
        <td className="px-6 py-4 text-sm text-muted-foreground">
          {formatDateTime(issue.createdAt)}
        </td>
      )}
      <td className="px-6 py-4">
        <div className="flex items-center justify-center gap-1" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            onClick={() => navigate(`/qa/issues/${issue.id}`)}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-[hsl(210,50%,96%)] hover:text-foreground"
            title="View"
          >
            <Bug className="h-[14px] w-[14px]" strokeWidth={1.5} />
          </button>
          <button
            type="button"
            onClick={() => onEdit(issue)}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-[hsl(210,50%,96%)] hover:text-foreground"
            title="Edit"
          >
            <svg className="h-[14px] w-[14px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.83 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/></svg>
          </button>
          <button
            type="button"
            onClick={() => onDuplicate(issue)}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-[hsl(210,50%,96%)] hover:text-foreground"
            title="Duplicate"
          >
            <Copy className="h-[14px] w-[14px]" strokeWidth={1.5} />
          </button>
          <button
            type="button"
            onClick={() => onDelete(issue.id, issue.title)}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-red-50 hover:text-red-600"
            title="Delete"
          >
            <svg className="h-[14px] w-[14px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>
          </button>
        </div>
      </td>
    </tr>
  );
}

export default function IssueListPage() {
  const [search, setSearch] = useState("");
  const [productFilter, setProductFilter] = useState("");
  const [moduleFilter, setModuleFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [assigneeFilter, setAssigneeFilter] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [duplicateTarget, setDuplicateTarget] = useState<IssueListItem | null>(null);
  const [editTarget, setEditTarget] = useState<IssueListItem | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

  const { visibility, toggle: toggleColumn, reset: resetColumns, isVisible, minReached } =
    useColumnVisibility("issues", ISSUE_COLUMNS);

  const deleteIssue = useDeleteIssue();

  const { data: result, isLoading, isError, error } = useIssueList({
    page,
    limit,
    search: search.trim() || undefined,
    productId: productFilter || undefined,
    moduleId: moduleFilter || undefined,
    category: categoryFilter || undefined,
    priority: priorityFilter || undefined,
    status: statusFilter || undefined,
    assigneeId: assigneeFilter || undefined,
  });

  const { data: products = [] } = useIssueProducts();
  const { data: modules = [] } = useProductModules(productFilter || null);
  const { data: allUsers = [] } = useUserOptions();
  const { data: categoryItems = [] } = useCategoryItems("issue_category");
  const { data: priorityItems = [] } = useCategoryItems("issue_priority");
  const { data: statusItems = [] } = useCategoryItems("issue_status");

  const issues = result?.data ?? [];
  const pagination = result?.pagination ?? { page: 1, limit, total: 0, totalPages: 1 };
  const total = pagination.total;

  const moduleFilterOptions = useMemo(
    () => modules.filter((m) => m.isActive),
    [modules],
  );

  function resetPage<T>(setter: (v: T) => void) {
    return (value: T) => {
      setter(value);
      setPage(1);
    };
  }

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    await deleteIssue.mutateAsync(deleteTarget.id);
    showToast(`${deleteTarget.name} deleted successfully`);
  }

  return (
    <AppLayout
      breadcrumb={[
        { label: "PhazeX Hub", href: "/" },
        { label: "QA" },
        { label: "Issues" },
      ]}
    >
      <div className="w-full space-y-6">
        {toast && (
          <div
            className="fixed right-6 top-16 z-[9999] rounded-lg border bg-white px-4 py-3 text-sm font-medium text-green-700 shadow-lg"
            style={{ borderColor: "hsl(142,55%,80%)" }}
          >
            {toast}
          </div>
        )}

        {/* Page header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Bug className="h-[18px] w-[18px] text-foreground" strokeWidth={1.5} />
            <h1 className="text-2xl font-semibold tracking-tight">Issues</h1>
          </div>
          <div className="flex items-center gap-2">
            <ColumnSelectorButton
              columns={ISSUE_COLUMNS}
              visibility={visibility}
              onToggle={toggleColumn}
              onReset={resetColumns}
              minReached={minReached}
            />
            <Button onClick={() => setDrawerOpen(true)}>
              <Plus className="h-4 w-4" strokeWidth={1.5} />
              New Issue
            </Button>
          </div>
        </div>

        {isError ? (
          <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-6 text-sm text-destructive">
            <div className="font-medium">Failed to load issues</div>
            <div className="mt-1 font-mono text-xs opacity-80">
              {(error as Error)?.message ?? "Unknown error"}
            </div>
          </div>
        ) : (
          <>
            {/* Filters */}
            <div className="mb-3 flex flex-wrap items-center gap-3">
              <div className="relative w-[280px]">
                <Search
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <Input
                  placeholder="Search issues..."
                  value={search}
                  onChange={(e) => resetPage(setSearch)(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select
                value={productFilter}
                onChange={(e) => {
                  resetPage(setProductFilter)(e.target.value);
                  setModuleFilter("");
                }}
                className="w-48"
              >
                <option value="">All Products</option>
                {products.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </Select>
              <Select
                value={moduleFilter}
                onChange={(e) => resetPage(setModuleFilter)(e.target.value)}
                className="w-44"
                disabled={!productFilter}
              >
                <option value="">
                  {productFilter ? "All Modules" : "Select a product first"}
                </option>
                {moduleFilterOptions.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </Select>
              <Select
                value={categoryFilter}
                onChange={(e) => resetPage(setCategoryFilter)(e.target.value)}
                className="w-40"
              >
                <option value="">All Categories</option>
                {categoryItems.map((c) => (
                  <option key={c.id} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </Select>
              <Select
                value={priorityFilter}
                onChange={(e) => resetPage(setPriorityFilter)(e.target.value)}
                className="w-36"
              >
                <option value="">All Priorities</option>
                {priorityItems.map((p) => (
                  <option key={p.id} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </Select>
              <Select
                value={statusFilter}
                onChange={(e) => resetPage(setStatusFilter)(e.target.value)}
                className="w-40"
              >
                <option value="">All Status</option>
                {statusItems.map((s) => (
                  <option key={s.id} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </Select>
              <Select
                value={assigneeFilter}
                onChange={(e) => resetPage(setAssigneeFilter)(e.target.value)}
                className="w-44"
              >
                <option value="">All Assignees</option>
                {allUsers.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </Select>
            </div>

            {/* Table view */}
            <div
              className="overflow-hidden rounded-xl border bg-card"
              style={{ borderColor: "hsl(210,30%,85%)" }}
            >
              {isLoading ? (
                <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" strokeWidth={1.5} />
                  Loading issues...
                </div>
              ) : issues.length === 0 ? (
                <div className="py-16 text-center text-sm text-muted-foreground">
                  No issues match the current filters.
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-[hsl(210,30%,97%)] text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    <tr>
                      {isVisible("code") && <th className="px-6 py-3">#</th>}
                      {isVisible("title") && <th className="px-6 py-3">Title</th>}
                      {isVisible("productName") && <th className="px-6 py-3">Product</th>}
                      {isVisible("moduleName") && <th className="px-6 py-3">Module</th>}
                      {isVisible("category") && <th className="px-6 py-3">Category</th>}
                      {isVisible("priority") && <th className="px-6 py-3">Priority</th>}
                      {isVisible("status") && <th className="px-6 py-3">Status</th>}
                      {isVisible("assigneeName") && <th className="px-6 py-3">Assignee</th>}
                      {isVisible("reportedByName") && <th className="px-6 py-3">Reported By</th>}
                      {isVisible("createdAt") && <th className="px-6 py-3">Created</th>}
                      <th className="px-6 py-3 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {issues.map((i) => (
                      <IssueRow
                        key={i.id}
                        issue={i}
                        onDelete={(id, name) => setDeleteTarget({ id, name })}
                        onDuplicate={setDuplicateTarget}
                        onEdit={setEditTarget}
                        isVisible={isVisible}
                      />
                    ))}
                  </tbody>
                </table>
              )}

              {/* Pagination footer */}
              <div data-pagination="true" style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '9px 14px',
                borderTop: '1px solid hsl(210,30%,85%)',
                background: 'hsl(210,30%,97%)',
                fontSize: '12px',
                color: 'hsl(215,16%,47%)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span>
                    Showing {total === 0 ? 0 : (page - 1) * limit + 1}–{Math.min(page * limit, total)} of {total} entries
                  </span>
                  <span style={{ marginLeft: '8px' }}>Show</span>
                  <select
                    value={limit}
                    onChange={e => { setLimit(Number(e.target.value)); setPage(1) }}
                    style={{
                      height: '26px',
                      padding: '0 4px',
                      border: '1px solid hsl(210,30%,85%)',
                      borderRadius: '4px',
                      fontSize: '12px',
                      background: 'white',
                      cursor: 'pointer',
                    }}
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </select>
                  <span>entries</span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <button
                    onClick={() => setPage(p => p - 1)}
                    disabled={page === 1}
                    style={{
                      width: '28px', height: '28px',
                      border: '1px solid hsl(210,30%,85%)',
                      borderRadius: '4px',
                      background: 'white',
                      cursor: page === 1 ? 'not-allowed' : 'pointer',
                      opacity: page === 1 ? 0.4 : 1,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >‹</button>
                  <span style={{ padding: '0 10px' }}>
                    {Math.ceil(total / limit) === 0 ? '0' : page} / {Math.ceil(total / limit) || 1}
                  </span>
                  <button
                    onClick={() => setPage(p => p + 1)}
                    disabled={page >= Math.ceil(total / limit)}
                    style={{
                      width: '28px', height: '28px',
                      border: '1px solid hsl(210,30%,85%)',
                      borderRadius: '4px',
                      background: 'white',
                      cursor: page >= Math.ceil(total / limit) ? 'not-allowed' : 'pointer',
                      opacity: page >= Math.ceil(total / limit) ? 0.4 : 1,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >›</button>
                </div>
              </div>
            </div>
          </>
        )}

        <NewIssueDrawer
          isOpen={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          onSaved={(code) => showToast(`Issue created: ${code}`)}
        />

        <NewIssueDrawer
          isOpen={!!duplicateTarget}
          onClose={() => setDuplicateTarget(null)}
          duplicateFrom={duplicateTarget ?? undefined}
          onSaved={(code) => showToast(`Issue created: ${code}`)}
        />

        {editTarget && (
          <EditIssueDrawerLoader
            issueId={editTarget.id}
            onClose={() => setEditTarget(null)}
            onSaved={(code) => showToast(`Issue updated: ${code}`)}
          />
        )}

        <ConfirmDialog
          open={!!deleteTarget}
          onOpenChange={(o) => !o && setDeleteTarget(null)}
          title="Delete issue?"
          description={
            deleteTarget
              ? `Are you sure you want to delete '${deleteTarget.name}'? It will be removed from the Issues list.`
              : ""
          }
          onConfirm={confirmDelete}
        />
      </div>
    </AppLayout>
  );
}

// The edit drawer needs the full IssueDetail (for description + reporter). Load
// it on demand when an edit is requested from the list row.
function EditIssueDrawerLoader({
  issueId,
  onClose,
  onSaved,
}: {
  issueId: string;
  onClose: () => void;
  onSaved: (code: string) => void;
}) {
  const { data: issue } = useIssue(issueId);
  if (!issue) return null;
  return (
    <NewIssueDrawer
      isOpen
      onClose={onClose}
      editIssue={issue}
      onSaved={onSaved}
    />
  );
}
